import React, { useState } from 'react';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { client } from '../../api/Api';
import { Link } from 'react-router-dom';
import { Button, TextField } from '@mui/material';

export default function AdminOpenFoodFacts() {
  const [limit, setLimit] = useState(10);
  const [productIds, setProductIds] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info'); // 'info', 'success', 'error'

  const processAllProducts = async () => {
    if (limit < 0) {
      setMessage('Limit negali būti neigiamas');
      setMessageType('error');
      return;
    }

    setLoading(true);
    setMessage('');
    try {
      const res = await client.get('/admin/openfoodfacts/process', {
        params: limit > 0 ? { limit } : {}
      });
      const data = res.data || {};
      if (data.success) {
        setMessage(data.message || 'Procesavimas pradėtas. Patikrinkite serverio logus progresui.');
        setMessageType('success');
      } else {
        setMessage(data.error || 'Nepavyko pradėti procesavimo');
        setMessageType('error');
      }
    } catch (e) {
      setMessage(e?.response?.data?.error || e?.message || 'Nepavyko pradėti procesavimo');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const processProductsByIds = async () => {
    const idsText = productIds.trim();
    if (!idsText) {
      setMessage('Įveskite produktų ID (atskirtus kableliais)');
      setMessageType('error');
      return;
    }

    const ids = idsText
      .split(',')
      .map(id => id.trim())
      .filter(id => id)
      .map(id => {
        const num = parseInt(id, 10);
        if (isNaN(num)) {
          throw new Error(`Netinkamas ID: ${id}`);
        }
        return num;
      });

    if (ids.length === 0) {
      setMessage('Nerasta galiojančių ID');
      setMessageType('error');
      return;
    }

    setLoading(true);
    setMessage('');
    try {
      const res = await client.post('/admin/openfoodfacts/process-by-ids', ids);
      const data = res.data || {};
      if (data.success) {
        setMessage(data.message || `Procesavimas pradėtas ${ids.length} produktams. Patikrinkite serverio logus progresui.`);
        setMessageType('success');
      } else {
        setMessage(data.error || 'Nepavyko pradėti procesavimo');
        setMessageType('error');
      }
    } catch (e) {
      setMessage(e?.response?.data?.error || e?.message || 'Nepavyko apdoroti produktų');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <nav className="text-sm" aria-label="Breadcrumb">
        <ol className="mb-2 flex items-center gap-2 text-slate-500">
          <li><Link to="/admin/dashboard" className="hover:text-slate-700">Admin</Link></li>
          <li aria-hidden className="text-slate-300">/</li>
          <li className="text-slate-700">Open Food Facts</li>
        </ol>
      </nav>

      <div className="rounded-md border p-4">
        <h2 className="font-semibold mb-3">Open Food Facts - Produktų duomenų gavimas</h2>
        <p className="text-sm text-slate-600 mb-4">
          Šis įrankis leidžia automatiškai gauti produkto duomenis iš Open Food Facts API pagal produkto pavadinimą.
          Duomenys apima: pavadinimą, ingredientus, maistinę vertę, nuotraukas ir kitą informaciją.
        </p>

        {message && (
          <Alert 
            severity={messageType === 'success' ? 'success' : messageType === 'error' ? 'error' : 'info'}
            sx={{ mb: 3 }}
            onClose={() => setMessage('')}
          >
            {message}
          </Alert>
        )}

        <div className="space-y-4">
          {/* Process all products section */}
          <div className="rounded border p-4 bg-slate-50">
            <h3 className="font-semibold mb-3">Apdoroti visus produktus</h3>
            <p className="text-sm text-slate-600 mb-3">
              Apdoroja visus produktus iš duomenų bazės, kurie turi pavadinimą. 
              Kiekvienam produktui bandoma rasti atitikmenį Open Food Facts duomenų bazėje pagal pavadinimą.
            </p>
            <div className="flex flex-wrap gap-2 items-center">
              <TextField
                label="Limit (produktų skaičius)"
                type="number"
                value={limit}
                onChange={(e) => setLimit(parseInt(e.target.value, 10) || 0)}
                size="small"
                sx={{ width: 200 }}
                helperText="Palikite tuščią arba 0, jei norite apdoroti visus produktus"
              />
              <Button
                variant="contained"
                color="primary"
                onClick={processAllProducts}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Apdorojama...' : 'Apdoroti produktus'}
              </Button>
            </div>
          </div>

          {/* Process by IDs section */}
          <div className="rounded border p-4 bg-slate-50">
            <h3 className="font-semibold mb-3">Apdoroti konkretius produktus pagal ID</h3>
            <p className="text-sm text-slate-600 mb-3">
              Apdoroja tik nurodytus produktus pagal jų ID. Įveskite ID, atskirtus kableliais (pvz.: 1, 2, 3).
            </p>
            <div className="flex flex-wrap gap-2 items-center">
              <TextField
                label="Produktų ID (atskirti kableliais)"
                value={productIds}
                onChange={(e) => setProductIds(e.target.value)}
                size="small"
                sx={{ flex: 1, minWidth: 300 }}
                placeholder="pvz.: 1, 2, 3, 10, 25"
                helperText="Įveskite produktų ID, atskirtus kableliais"
              />
              <Button
                variant="contained"
                color="secondary"
                onClick={processProductsByIds}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Apdorojama...' : 'Apdoroti pagal ID'}
              </Button>
            </div>
          </div>
        </div>

        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2, mt: 2 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              Apdorojami produktai iš Open Food Facts... Tai gali užtrukti keletą minučių.
            </Typography>
          </Box>
        )}
      </div>

      <div className="rounded-md border p-4 bg-blue-50">
        <h3 className="font-semibold mb-2">Informacija</h3>
        <ul className="text-sm text-slate-700 space-y-1 list-disc list-inside">
          <li>Service automatiškai ieško produktų Open Food Facts duomenų bazėje pagal produkto pavadinimą</li>
          <li>Naudojamas produkto pavadinimas angliškai (nameEN), jei jis yra, kitu atveju - lietuviškas pavadinimas (name)</li>
          <li>Jei randamas atitikmuo, produktas atnaujinamas su duomenimis: pavadinimas, ingredientai, maistinė vertė, nuotraukos</li>
          <li>API užklausos turi 100ms delą tarp užklausų, kad neperkrautumėte Open Food Facts API</li>
          <li>Procesavimas gali užtrukti ilgai, jei apdorojate daug produktų</li>
        </ul>
      </div>
    </div>
  );
}

